import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SalaryTemplatePipelineBar from "./SalaryTemplatePipelineBar";
import SalaryTemplatePipelineReview from "./SalaryTemplatePipelineReview";
import SalaryTemplatePipelineAllocate from "./SalaryTemplatePipelineAllocate";
import SalaryTemplatePipelineCtc from "./SalaryTemplatePipelineCtc";
import {
  PIPELINE_STEPS,
  isPipelineActive,
  parsePipelineStep,
  pipelineStepPath,
  readPipelineState,
  writePipelineState,
} from "./salaryTemplatePipeline";
import "../../../../../styles/salary-templates-premium.css";
import "../../../../../styles/salary-template-pipeline.css";

/**
 * Create / edit salary structure wizard. Rendered on the salary structures page.
 */
export default function SalaryStructurePipelineHost() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pipelineStep = parsePipelineStep(searchParams);
  const inPipeline = isPipelineActive() && pipelineStep != null;
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!inPipeline || pipelineStep !== PIPELINE_STEPS.template) return;
    const state = readPipelineState();
    setTemplateName(state.templateName || "");
    setTemplateDesc(state.templateDescription || "");
    writePipelineState({ currentStep: PIPELINE_STEPS.template });
  }, [inPipeline, pipelineStep]);

  if (!inPipeline) return null;

  if (pipelineStep === PIPELINE_STEPS.ctcAllocation) {
    return (
      <div className="stp">
        <SalaryTemplatePipelineCtc />
      </div>
    );
  }

  if (pipelineStep === PIPELINE_STEPS.components) {
    return (
      <div className="stp stp--catalog">
        <SalaryTemplatePipelineAllocate />
      </div>
    );
  }

  if (pipelineStep === PIPELINE_STEPS.review) {
    return (
      <div className="stp">
        <SalaryTemplatePipelineReview />
      </div>
    );
  }

  function continueFromName() {
    const name = templateName.trim();
    if (!name) {
      setError("Salary structure name is required");
      return;
    }
    const prev = readPipelineState();
    writePipelineState({
      templateId: prev.editMode ? prev.templateId : null,
      templateName: name,
      templateDescription: templateDesc.trim(),
      currentStep: PIPELINE_STEPS.components,
    });
    const next = readPipelineState();
    if (next.returnToReview) {
      writePipelineState({ returnToReview: false, currentStep: PIPELINE_STEPS.review });
      navigate(pipelineStepPath(PIPELINE_STEPS.review));
      return;
    }
    navigate(pipelineStepPath(PIPELINE_STEPS.components, next));
  }

  return (
    <div className="stp">
      <SalaryTemplatePipelineBar currentStep={PIPELINE_STEPS.template} />
      <section className="stp-card">
        <h2 className="stp-section-title">Salary structure</h2>
        <p className="stp-section-hint">Name this structure. Existing components are selected on the next step.</p>
        <label className="stp-label" htmlFor="stp-pipeline-name">Structure name</label>
        <input
          id="stp-pipeline-name"
          className="stp-input"
          placeholder="e.g. Monthly Standard Salary"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
        />
        <label className="stp-label" htmlFor="stp-pipeline-desc">
          Description <span className="stp-optional">(optional)</span>
        </label>
        <input
          id="stp-pipeline-desc"
          className="stp-input"
          placeholder="Who is this structure for?"
          value={templateDesc}
          onChange={(e) => setTemplateDesc(e.target.value)}
        />
        {error ? <p className="stp-error">{error}</p> : null}
        <div className="stp-pipeline-sticky">
          <button type="button" className="stp-btn stp-btn-ghost" onClick={() => navigate("/salary-v2/structures")}>
            Back
          </button>
          <button type="button" className="stp-btn stp-btn-primary" onClick={continueFromName}>
            Next: Components
          </button>
        </div>
      </section>
    </div>
  );
}
